import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { type RemoteConfigAdapter } from '../domain/RemoteConfig'
import { UnleashAdapter } from '../infrastructure/UnleashAdapter'

type RemoteConfigContextValue = {
  ready: boolean
  adapter: RemoteConfigAdapter
}

const RemoteConfigContext = createContext<RemoteConfigContextValue | null>(null)
RemoteConfigContext.displayName = 'RemoteConfigContext'

const unleashAdapter = new UnleashAdapter()

type ConfigProviderProps = {
  adapter?: RemoteConfigAdapter
  children: ReactNode
}

export function ConfigProvider({ adapter = unleashAdapter, children }: ConfigProviderProps) {
  const [ready, setIsReady] = useState(adapter.ready)

  useEffect(
    function initAdapter() {
      adapter.init()
    },
    [adapter]
  )

  useEffect(
    function handleAdapterReady() {
      const unsubscribeReady = adapter.subscribe('ready', () => {
        setIsReady(adapter.ready)
      })
      setIsReady(adapter.ready)

      return () => {
        unsubscribeReady()
      }
    },
    [adapter]
  )

  const value = useMemo(() => ({ ready, adapter }), [ready, adapter])

  return <RemoteConfigContext.Provider value={value}>{children}</RemoteConfigContext.Provider>
}

export function useRemoteConfig() {
  const context = useContext(RemoteConfigContext)
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider')
  }
  return context
}
